import React, { useState, useEffect, useContext } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { Search, Filter, Edit2, X, Check, ChevronDown, Palette, RefreshCw, Trash2, Scissors, Share2, Package, Mail } from 'lucide-react';
import AppHeader from '../components/AppHeader';
import { NotifyCustomerModal } from '../components/NotifyCustomerModal';
import TimestampedNotes from '../components/TimestampedNotes';
import { OrderShippingCard } from '../components/OrderShippingCard';
import { brandName, brandEmail, brandPhone, brandLogo, brandWebsite } from '../lib/brand';

// Decoration method options for the inline editor on each design tile.
const DECO_METHODS = [
  { value: 'screen_print', label: 'Screen Print', short: 'SCREEN PRINT', emoji: '🖨' },
  { value: 'embroidery', label: 'Embroidery', short: 'EMBROIDERY', emoji: '🧵' },
  { value: 'dtf', label: 'DTF Transfer', short: 'DTF', emoji: '✨' },
  { value: 'dtg', label: 'DTG', short: 'DTG', emoji: '🎨' },
  { value: 'sublimation', label: 'Sublimation', short: 'SUBLIMATION', emoji: '🌈' },
  { value: 'heat_transfer', label: 'Heat Transfer', short: 'HEAT', emoji: '🔥' },
  { value: 'vinyl', label: 'Vinyl', short: 'VINYL', emoji: '🪧' },
];
const decoLabel = (v) => DECO_METHODS.find((m) => m.value === v) || { short: (v || 'NONE').toUpperCase(), emoji: '•' };

// Per-item purchasing status configuration. Each status has a colour set and
// the next logical action so operators can advance items with a single click.
const PURCHASING_STATUSES = [
  { value: 'needs_purchasing', label: 'Needs Purchasing', short: 'TO ORDER',  bg: '#fef2f2', fg: '#b91c1c', border: '#fecaca', emoji: '🛒' },
  { value: 'purchased',        label: 'Purchased',        short: 'ORDERED',   bg: '#fef9c3', fg: '#854d0e', border: '#fde68a', emoji: '📦' },
  { value: 'received',         label: 'Received',         short: 'RECEIVED',  bg: '#dcfce7', fg: '#166534', border: '#bbf7d0', emoji: '✓' },
  { value: 'in_stock',         label: 'In Stock',         short: 'IN STOCK',  bg: '#dbeafe', fg: '#1e40af', border: '#bfdbfe', emoji: '🏷' },
];
const purchStatusMeta = (v) => PURCHASING_STATUSES.find((s) => s.value === v) || PURCHASING_STATUSES[0];

// Build a compact decoration label: if every used location uses the same method, show that method.
// Otherwise show "Front: EMB · Back: SP" style breakdown.
const buildDecorationSummary = (item) => {
  const primary = item.print_method || 'screen_print';
  const locs = Array.isArray(item.print_locations) ? item.print_locations : [];
  const overrides = item.location_methods || {};
  if (locs.length === 0) return { mixed: false, primary, breakdown: [] };
  const effective = locs.map((loc) => overrides[loc] || overrides[loc.toLowerCase()] || primary);
  const allSame = effective.every((m) => m === effective[0]);
  if (allSame) return { mixed: false, primary: effective[0], breakdown: [] };
  return { mixed: true, primary, breakdown: locs.map((loc, i) => ({ loc, method: effective[i] })) };
};

const ORDER_STATUSES = ['Pending', 'Ordered', 'In Production', 'Ready for Pickup', 'Shipped', 'Delivered'];
const ORDER_TYPES = [
  { value: 'SP', label: 'Screen Print' },
  { value: 'EMB', label: 'Embroidery' },
  { value: 'DTG', label: 'Direct to Garment' }
];
const GOODS_RECEIVED_OPTIONS = ['No', 'Yes', 'Partial'];
const ART_APPROVED_OPTIONS = ['No', 'Yes'];

const FILTER_OPTIONS = [
  { value: 'all', label: 'All Orders' },
  { value: 'pending', label: 'Pending Orders' },
  { value: 'open', label: 'Open Orders' },
  { value: 'ready', label: 'Ready for Pickup' },
  { value: 'completed', label: 'Completed Orders' },
  { value: 'canceled', label: 'Canceled Orders' }
];

const OrderTracking = () => {
  const { user, logout } = useContext(AuthContext);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterType, setFilterType] = useState('all');
  const [showFilterDropdown, setShowFilterDropdown] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedOrders, setSelectedOrders] = useState([]);
  const [editingOrder, setEditingOrder] = useState(null);
  const [notifyOrder, setNotifyOrder] = useState(null);
  const [editForm, setEditForm] = useState({});
  // Map orderId -> [{item_index, brand, style, color, quantity, thumbnail, approval_status}]
  const [orderThumbnails, setOrderThumbnails] = useState({});
  // Map orderId -> purchasing items (per-item status: needs_purchasing | purchased | received | in_stock)
  const [orderPurchasing, setOrderPurchasing] = useState({});
  // Map orderId -> CPP line items (the proposal items flagged `is_cpp:true`)
  // These don't get purchasing_items rows, so we surface them separately
  // with a completion checkmark tied to `order.status === 'Completed'`.
  const [orderCppItems, setOrderCppItems] = useState({});

  // Check for filter / search in URL on mount
  useEffect(() => {
    const filter = searchParams.get('filter');
    if (filter === 'pending') {
      setFilterType('pending');
    }
    const search = searchParams.get('search');
    if (search) setSearchTerm(search);
  }, [searchParams]);

  useEffect(() => {
    fetchOrders();
  }, []);

  const fetchOrders = async () => {
    try {
      const response = await axios.get('/orders');
      const list = response.data || [];
      setOrders(list);
      // Fan-out per-order detail fetches in small batches (6 orders at a time) so a long list
      // doesn't fire hundreds of simultaneous requests and trip the edge rate limiter.
      const thumbMap = {};
      const purchMap = {};
      const cppMap = {};
      const fetchOne = async (o) => {
          if (!o.proposal_id) return;
          try {
            const r = await axios.get(`/proposals/${o.proposal_id}/mockup-thumbnails`);
            thumbMap[o.id] = r.data?.items || [];
          } catch {
            thumbMap[o.id] = [];
          }
          try {
            const r2 = await axios.get(`/purchasing/by-order/${o.id}`);
            purchMap[o.id] = r2.data || [];
          } catch {
            purchMap[o.id] = [];
          }
          try {
            const r3 = await axios.get(`/orders/${o.id}/cpp-items`);
            cppMap[o.id] = r3.data || [];
          } catch {
            cppMap[o.id] = [];
          }
      };
      for (let i = 0; i < list.length; i += 6) {
        await Promise.all(list.slice(i, i + 6).map(fetchOne));
        setOrderThumbnails({ ...thumbMap });
        setOrderPurchasing({ ...purchMap });
        setOrderCppItems({ ...cppMap });
      }
    } catch (error) {
      toast.error('Failed to load orders');
    } finally {
      setLoading(false);
    }
  };

  const refreshOrderPurchasing = async (orderId) => {
    try {
      const r = await axios.get(`/purchasing/by-order/${orderId}`);
      setOrderPurchasing((prev) => ({ ...prev, [orderId]: r.data || [] }));
    } catch { /* non-fatal */ }
  };

  const changePurchasingStatus = async (orderId, itemId, newStatus) => {
    try {
      await axios.post('/purchasing/update-status', { item_ids: [itemId], status: newStatus });
      toast.success(`Marked ${purchStatusMeta(newStatus).label}`);
      await refreshOrderPurchasing(orderId);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update status');
    }
  };

  const refreshOrderThumbs = async (order) => {
    if (!order?.proposal_id) return;
    try {
      const r = await axios.get(`/proposals/${order.proposal_id}/mockup-thumbnails`);
      setOrderThumbnails((prev) => ({ ...prev, [order.id]: r.data?.items || [] }));
    } catch { /* non-fatal */ }
  };

  // Inline change of decoration method from the order tracking tile.
  // Asks via window.confirm whether to re-price (matches the ProductMockupCard flow).
  // Clears any per-location overrides so the picked method applies to EVERY location
  // (otherwise the item would still render as "MIXED" because old overrides linger).
  const changeDecoration = async (order, itemIndex, newMethod) => {
    const reprice = window.confirm(
      'Re-price the proposal now?\n\nOK = re-price using the new decoration method.\nCancel = keep current pricing.'
    );
    try {
      await axios.patch(
        `/proposals/${order.proposal_id}/items/${itemIndex}/decoration`,
        { print_method: newMethod, location_methods: {}, reprice }
      );
      toast.success(reprice ? 'Decoration updated · proposal repriced' : 'Decoration updated');
      await refreshOrderThumbs(order);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update decoration');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const getFilteredOrders = () => {
    let filtered = orders;

    if (filterType === 'pending') {
      filtered = filtered.filter(o => o.status === 'Pending');
    } else if (filterType === 'open') {
      filtered = filtered.filter(o => ['Pending', 'Ordered', 'In Production'].includes(o.status));
    } else if (filterType === 'ready') {
      filtered = filtered.filter(o => o.status === 'Ready for Pickup');
    } else if (filterType === 'completed') {
      // "Completed" filter shows BOTH terminal states so the archive view
      // is comprehensive — Completed (production done) AND Delivered.
      filtered = filtered.filter(o => ['Completed', 'Delivered'].includes(o.status));
    } else if (filterType === 'canceled') {
      filtered = filtered.filter(o => o.status === 'Canceled');
    } else {
      // 'all' = active orders only (hide completed/delivered/canceled by
      // default to keep the active queue clean). Search still falls
      // through to the if-branch below so admins can find completed
      // orders by PO # / customer name without switching tabs.
      if (!searchTerm) {
        filtered = filtered.filter(o => !['Completed', 'Delivered', 'Canceled'].includes(o.status));
      }
    }

    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      // When a search term is present we scan ALL orders regardless of
      // filterType so the admin can pull up a completed order from the
      // main Orders list without flipping the filter to "Completed".
      filtered = orders.filter(o =>
        (o.po_number || '').toLowerCase().includes(search) ||
        (o.customer_name || '').toLowerCase().includes(search) ||
        (o.ordered_by || '').toLowerCase().includes(search)
      );
    }

    return filtered;
  };

  const toggleOrderSelection = (orderId) => {
    setSelectedOrders(prev => 
      prev.includes(orderId) 
        ? prev.filter(id => id !== orderId)
        : [...prev, orderId]
    );
  };

  const startEditing = (order) => {
    setEditingOrder(order.id);
    setEditForm({
      po_number: order.po_number || '',
      ordered_by: order.ordered_by || '',
      date_ordered: order.date_ordered ? order.date_ordered.split('T')[0] : '',
      order_type: order.order_type || 'SP',
      status: order.status || 'Pending',
      eta: order.eta ? order.eta.split('T')[0] : '',
      due_date: order.due_date ? order.due_date.split('T')[0] : '',
      date_received: order.date_received ? order.date_received.split('T')[0] : '',
      goods_received: order.goods_received || 'No',
      art_approved: order.art_approved || 'No',
      tracking_number: order.tracking_number || '',
      notes: order.notes || ''
    });
  };

  const cancelEditing = () => {
    setEditingOrder(null);
    setEditForm({});
  };

  const saveOrder = async (orderId) => {
    try {
      await axios.put(`/orders/${orderId}`, editForm);
      toast.success('Order updated successfully');
      setEditingOrder(null);
      setEditForm({});
      fetchOrders();
    } catch (error) {
      toast.error('Failed to update order');
    }
  };

  const getStatusStyle = (status) => {
    const styles = {
      'Pending': { bg: '#fef3c7', color: '#92400e', text: 'PENDING' },
      'Ordered': { bg: '#dbeafe', color: '#1e40af', text: 'ORDERED' },
      'In Production': { bg: '#e0e7ff', color: '#5b21b6', text: 'IN PRODUCTION' },
      'Ready for Pickup': { bg: '#dcfce7', color: '#166534', text: 'READY FOR PICKUP' },
      'Shipped': { bg: '#d1fae5', color: '#065f46', text: 'SHIPPED' },
      'Delivered': { bg: '#d1fae5', color: '#065f46', text: 'DELIVERED' }
    };
    return styles[status] || { bg: '#f3f4f6', color: '#374151', text: status?.toUpperCase() };
  };

  const filteredOrders = getFilteredOrders();
  const currentFilter = FILTER_OPTIONS.find(f => f.value === filterType);

  return (
    <div className="dashboard" data-testid="order-tracking-page">
      {/* Header - Shared AppHeader */}
      <AppHeader />
      {notifyOrder && <NotifyCustomerModal order={notifyOrder} onClose={() => setNotifyOrder(null)} onSent={() => fetchOrders()} />}

      <main className="dashboard-content">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
          <h1 className="dashboard-title" style={{ marginBottom: 0 }}>Order Tracking</h1>
          
          <div style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
            {/* Filter Dropdown */}
            <div style={{ position: 'relative' }}>
              <button
                onClick={() => setShowFilterDropdown(!showFilterDropdown)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '10px 16px',
                  background: 'white',
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500'
                }}
                data-testid="filter-dropdown"
              >
                {currentFilter?.label}
                <ChevronDown size={16} />
              </button>
              
              {showFilterDropdown && (
                <div style={{
                  position: 'absolute',
                  top: '100%',
                  right: 0,
                  marginTop: '4px',
                  background: 'white',
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px',
                  boxShadow: '0 10px 25px rgba(0,0,0,0.1)',
                  zIndex: 50,
                  minWidth: '180px'
                }}>
                  {FILTER_OPTIONS.map(option => (
                    <button
                      key={option.value}
                      onClick={() => {
                        setFilterType(option.value);
                        setShowFilterDropdown(false);
                      }}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '10px',
                        width: '100%',
                        padding: '10px 16px',
                        border: 'none',
                        background: filterType === option.value ? '#fef2f2' : 'white',
                        cursor: 'pointer',
                        fontSize: '14px',
                        textAlign: 'left'
                      }}
                    >
                      <span style={{
                        width: '16px',
                        height: '16px',
                        borderRadius: '50%',
                        border: filterType === option.value ? '5px solid #e74c3c' : '2px solid #d1d5db',
                        display: 'inline-block'
                      }} />
                      {option.label}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Search */}
            <div style={{ position: 'relative' }}>
              <Search size={16} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#9ca3af' }} />
              <Input
                placeholder="Search orders..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={{ paddingLeft: '36px', width: '220px' }}
                data-testid="search-input"
              />
            </div>
          </div>
        </div>

        {/* Orders List - Card Style */}
        {loading ? (
          <div style={{ textAlign: 'center', padding: '48px', color: '#6b7280' }}>Loading orders...</div>
        ) : filteredOrders.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '48px', color: '#6b7280', background: 'white', borderRadius: '12px' }}>
            <Package size={48} style={{ margin: '0 auto 16px', opacity: 0.3 }} />
            <p>No orders found.</p>
            <p style={{ fontSize: 13, marginTop: 6 }}>Orders are created automatically when a proposal is marked Approved (or when the customer approves art and pays the deposit).</p>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'center', marginTop: 16, flexWrap: 'wrap' }}>
              <Button onClick={() => navigate('/')} style={{ background: '#e74c3c' }}>
                Go to Proposals
              </Button>
              {(user?.role === 'admin' || user?.role === 'superadmin') && (
                <Button variant="outline" data-testid="orders-sync-from-proposals-btn" onClick={async () => {
                  try {
                    const { data } = await axios.post('/orders/sync-from-proposals');
                    toast.success(data.created ? `Created ${data.created} order(s) from approved proposals` : 'All approved proposals already have orders');
                    fetchOrders();
                  } catch (e) { toast.error(e?.response?.data?.detail || 'Sync failed'); }
                }}>Create orders from approved proposals</Button>
              )}
            </div>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {filteredOrders.map((order) => {
              const statusStyle = getStatusStyle(order.status);
              const isEditing = editingOrder === order.id;
              
              return (
                <div
                  key={order.id}
                  style={{
                    background: 'white',
                    borderRadius: '8px',
                    padding: '20px',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
                    borderLeft: '4px solid #e74c3c'
                  }}
                  data-testid={`order-card-${order.id}`}
                >
                  {isEditing ? (
                    /* Edit Mode */
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '16px' }}>
                        <div>
                          <label style={{ fontSize: '12px', color: '#6b7280', display: 'block', marginBottom: '4px' }}>PO Number</label>
                          <Input value={editForm.po_number} onChange={(e) => setEditForm({...editForm, po_number: e.target.value})} />
                        </div>
                        <div>
                          <label style={{ fontSize: '12px', color: '#6b7280', display: 'block', marginBottom: '4px' }}>Status</label>
                          <select 
                            value={editForm.status} 
                            onChange={(e) => setEditForm({...editForm, status: e.target.value})}
                            style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid #e5e7eb' }}
                          >
                            {ORDER_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
                          </select>
                        </div>
                        <div>
                          <label style={{ fontSize: '12px', color: '#6b7280', display: 'block', marginBottom: '4px' }}>Order Type</label>
                          <select 
                            value={editForm.order_type} 
                            onChange={(e) => setEditForm({...editForm, order_type: e.target.value})}
                            style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid #e5e7eb' }}
                          >
                            {ORDER_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                          </select>
                        </div>
                        <div>
                          <label style={{ fontSize: '12px', color: '#6b7280', display: 'block', marginBottom: '4px' }}>Ordered By</label>
                          <Input value={editForm.ordered_by} onChange={(e) => setEditForm({...editForm, ordered_by: e.target.value})} />
                        </div>
                      </div>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '16px' }}>
                        <div>
                          <label style={{ fontSize: '12px', color: '#6b7280', display: 'block', marginBottom: '4px' }}>Date Ordered</label>
                          <Input type="date" value={editForm.date_ordered} onChange={(e) => setEditForm({...editForm, date_ordered: e.target.value})} />
                        </div>
                        <div>
                          <label style={{ fontSize: '12px', color: '#6b7280', display: 'block', marginBottom: '4px' }}>Due Date</label>
                          <Input type="date" value={editForm.due_date} onChange={(e) => setEditForm({...editForm, due_date: e.target.value})} />
                        </div>
                        <div>
                          <label style={{ fontSize: '12px', color: '#6b7280', display: 'block', marginBottom: '4px' }}>ETA</label>
                          <Input type="date" value={editForm.eta} onChange={(e) => setEditForm({...editForm, eta: e.target.value})} />
                        </div>
                        <div>
                          <label style={{ fontSize: '12px', color: '#6b7280', display: 'block', marginBottom: '4px' }}>Date Received</label>
                          <Input type="date" value={editForm.date_received} onChange={(e) => setEditForm({...editForm, date_received: e.target.value})} />
                        </div>
                      </div>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '16px' }}>
                        <div>
                          <label style={{ fontSize: '12px', color: '#6b7280', display: 'block', marginBottom: '4px' }}>Art Approved</label>
                          <select 
                            value={editForm.art_approved} 
                            onChange={(e) => setEditForm({...editForm, art_approved: e.target.value})}
                            style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid #e5e7eb' }}
                          >
                            {ART_APPROVED_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
                          </select>
                        </div>
                        <div>
                          <label style={{ fontSize: '12px', color: '#6b7280', display: 'block', marginBottom: '4px' }}>Goods Received</label>
                          <select 
                            value={editForm.goods_received} 
                            onChange={(e) => setEditForm({...editForm, goods_received: e.target.value})}
                            style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid #e5e7eb' }}
                          >
                            {GOODS_RECEIVED_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
                          </select>
                        </div>
                        <div>
                          <label style={{ fontSize: '12px', color: '#6b7280', display: 'block', marginBottom: '4px' }}>Tracking #</label>
                          <Input value={editForm.tracking_number} onChange={(e) => setEditForm({...editForm, tracking_number: e.target.value})} />
                        </div>
                      </div>
                      <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                        <Button variant="outline" onClick={cancelEditing}>Cancel</Button>
                        <Button onClick={() => saveOrder(order.id)} style={{ background: '#e74c3c' }}>Save Changes</Button>
                      </div>
                    </div>
                  ) : (
                    /* Display Mode */
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: '16px' }}>
                      {/* Checkbox */}
                      <input
                        type="checkbox"
                        checked={selectedOrders.includes(order.id)}
                        onChange={() => toggleOrderSelection(order.id)}
                        style={{ width: '18px', height: '18px', marginTop: '4px', cursor: 'pointer', accentColor: '#e74c3c' }}
                      />
                      
                      {/* Order Info */}
                      <div style={{ flex: 1 }}>
                        <div style={{ marginBottom: '6px' }}>
                          <a
                            href="#"
                            onClick={(e) => { e.preventDefault(); navigate(`/proposal/${order.proposal_id}`); }}
                            style={{ color: '#e74c3c', textDecoration: 'none', fontSize: '15px', fontWeight: '600' }}
                          >
                            #{order.po_number || order.id.slice(0, 8)}
                          </a>
                        </div>
                        
                        <h3 style={{ fontSize: '17px', fontWeight: '700', margin: '0 0 4px 0', color: '#111827' }}>
                          {order.customer_name}
                        </h3>
                        <p style={{ fontSize: '13px', color: '#6b7280', margin: '0 0 12px 0' }}>
                          {brandName()}
                        </p>
                        
                        <div style={{ display: 'flex', gap: '32px', flexWrap: 'wrap' }}>
                          <div>
                            <p style={{ fontSize: '11px', color: '#9ca3af', margin: 0, textTransform: 'uppercase' }}>Order Date</p>
                            <p style={{ fontSize: '13px', fontWeight: '500', margin: '2px 0 0 0' }}>{formatDate(order.date_ordered || order.created_at)}</p>
                          </div>
                          <div>
                            <p style={{ fontSize: '11px', color: '#9ca3af', margin: 0, textTransform: 'uppercase' }}>Ship / Pick Up Date</p>
                            <p style={{ fontSize: '13px', fontWeight: '500', margin: '2px 0 0 0' }}>{formatDate(order.due_date || order.eta)}</p>
                          </div>
                          <div>
                            <p style={{ fontSize: '11px', color: '#9ca3af', margin: 0, textTransform: 'uppercase' }}>Order Total</p>
                            <p style={{ fontSize: '13px', fontWeight: '500', margin: '2px 0 0 0' }}>${(order.total_amount || 0).toFixed(2)}</p>
                          </div>
                          <div>
                            <p style={{ fontSize: '11px', color: '#9ca3af', margin: 0, textTransform: 'uppercase' }}>Quantity</p>
                            <p style={{ fontSize: '13px', fontWeight: '500', margin: '2px 0 0 0' }}>{order.quantity || 0} pcs</p>
                          </div>
                          <div>
                            <p style={{ fontSize: '11px', color: '#9ca3af', margin: 0, textTransform: 'uppercase' }}>Art Approved</p>
                            <p style={{ fontSize: '13px', fontWeight: '500', margin: '2px 0 0 0', color: order.art_approved === 'Yes' ? '#059669' : '#dc2626' }}>
                              {order.art_approved || 'No'}
                              {order.art_approved === 'Yes' && order.art_approved_at && (
                                <span style={{ fontSize: '10px', color: '#9ca3af', marginLeft: '4px' }}>
                                  ({formatDate(order.art_approved_at)})
                                </span>
                              )}
                            </p>
                          </div>
                        </div>

                        {order.eddm && (
                          <div style={{ marginTop: 14, background: '#fffbeb', border: '1px solid #fcd34d', borderRadius: 10, padding: '10px 14px' }} data-testid={`order-eddm-${order.id}`}>
                            <p style={{ fontSize: '11px', color: '#92400e', margin: 0, textTransform: 'uppercase', fontWeight: 700 }}>EDDM Direct Mail · {order.eddm.order_number} · {order.eddm.status_label}</p>
                            <div style={{ display: 'flex', gap: '24px', flexWrap: 'wrap', marginTop: 6, fontSize: 13 }}>
                              <span><span style={{ color: '#9ca3af' }}>Agent</span> <b>{order.eddm.agent}</b>{order.eddm.phone ? ` · ${order.eddm.phone}` : ''}</span>
                              <span><span style={{ color: '#9ca3af' }}>Brokerage</span> <b>{order.eddm.brokerage}</b></span>
                              <span><span style={{ color: '#9ca3af' }}>Mailing to</span> <b>{order.eddm.target_area}</b>{order.eddm.routes?.length ? ` · routes ${order.eddm.routes.map((r) => r.route_id).join(', ')}` : ''}</span>
                              <span><span style={{ color: '#9ca3af' }}>Pieces</span> <b>{(order.eddm.quantity || 0).toLocaleString()}</b> × {order.eddm.size} · {order.eddm.delivery_label}</span>
                            </div>
                            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginTop: 6, fontSize: 12 }}>
                              {order.eddm.proofs?.map((f) => <a key={f.url} href={`${axios.defaults.baseURL}${f.url}`} target="_blank" rel="noreferrer" style={{ color: '#b45309', fontWeight: 600 }}>Proof: {f.filename}</a>)}
                              {order.eddm.artwork?.map((f) => <a key={f.url} href={`${axios.defaults.baseURL}${f.url}`} target="_blank" rel="noreferrer" style={{ color: '#1d4ed8' }}>Artwork: {f.filename}</a>)}
                              {order.eddm.design_service && !order.eddm.proofs?.length && <span style={{ color: '#92400e' }}>Design service requested — proof pending</span>}
                              {order.eddm.mailing?.drop_date && <span>USPS drop {order.eddm.mailing.drop_date}{order.eddm.mailing.post_office ? ` · ${order.eddm.mailing.post_office}` : ''}</span>}
                            </div>
                          </div>
                        )}
                        {(orderThumbnails[order.id] || []).length > 0 && (
                          <div style={{ marginTop: 14 }} data-testid={`order-designs-${order.id}`}>
                            <p style={{ fontSize: '11px', color: '#9ca3af', margin: 0, textTransform: 'uppercase', marginBottom: 6 }}>
                              Approved Designs ({(orderThumbnails[order.id] || []).length})
                            </p>
                            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                              {(orderThumbnails[order.id] || []).map((it) => {
                                const src = it.thumbnail
                                  ? (it.thumbnail.startsWith('data:') ? it.thumbnail : `data:image/png;base64,${it.thumbnail}`)
                                  : null;
                                const approved = it.approval_status === 'approved';
                                const deco = buildDecorationSummary(it);
                                const primaryLabel = decoLabel(deco.primary);
                                return (
                                  <div
                                    key={it.item_index}
                                    style={{
                                      width: 110,
                                      background: '#ffffff',
                                      border: `2px solid ${approved ? '#16a34a' : '#e5e7eb'}`,
                                      borderRadius: 10,
                                      padding: 4,
                                      textAlign: 'center',
                                    }}
                                    data-testid={`order-design-tile-${order.id}-${it.item_index}`}
                                  >
                                    <button
                                      type="button"
                                      onClick={() => navigate(`/mockup-designer?proposal=${order.proposal_id}&item=${it.item_index}`)}
                                      title={`Click to open ${it.brand} ${it.style} (${it.color}) in the Mockup Designer`}
                                      style={{
                                        all: 'unset',
                                        position: 'relative',
                                        width: '100%',
                                        paddingBottom: '100%',
                                        background: '#fafafa',
                                        borderRadius: 6,
                                        overflow: 'hidden',
                                        display: 'block',
                                        cursor: 'pointer',
                                      }}
                                    >
                                      {src ? (
                                        <img src={src} alt={`${it.brand} ${it.style}`}
                                          style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'contain' }} />
                                      ) : (
                                        <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#cbd5e1', fontSize: 10, padding: 4 }}>
                                          No design yet
                                        </div>
                                      )}
                                      {approved && (
                                        <span style={{
                                          position: 'absolute', top: 4, right: 4,
                                          background: '#16a34a', color: '#fff',
                                          fontSize: 9, fontWeight: 700, padding: '1px 5px', borderRadius: 999,
                                        }}>✓</span>
                                      )}
                                    </button>
                                    <p style={{ fontSize: 10, color: '#475569', margin: '4px 0 0', fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                      {it.style} · {it.color}
                                    </p>
                                    <p style={{ fontSize: 9, color: '#94a3b8', margin: 0 }}>×{it.quantity}</p>

                                    {/* Decoration badge + inline editor */}
                                    <div style={{ marginTop: 4 }}>
                                      {deco.mixed ? (
                                        <div
                                          title={deco.breakdown.map((b) => `${b.loc}: ${decoLabel(b.method).short}`).join(' · ')}
                                          style={{
                                            display: 'inline-flex', flexWrap: 'wrap', gap: 2,
                                            background: '#fef3c7', color: '#92400e',
                                            borderRadius: 999, padding: '2px 6px',
                                            fontSize: 8, fontWeight: 700, lineHeight: 1.3,
                                          }}
                                          data-testid={`deco-badge-${order.id}-${it.item_index}`}>
                                          MIXED · {deco.breakdown.length} LOC
                                        </div>
                                      ) : (
                                        <div
                                          style={{
                                            background: '#ecfeff', color: '#0e7490',
                                            border: '1px solid #a5f3fc',
                                            borderRadius: 999, padding: '2px 6px',
                                            fontSize: 8, fontWeight: 700, letterSpacing: 0.3,
                                            display: 'inline-flex', alignItems: 'center', gap: 3,
                                          }}
                                          data-testid={`deco-badge-${order.id}-${it.item_index}`}>
                                          <span style={{ fontSize: 9 }}>{primaryLabel.emoji}</span>
                                          {primaryLabel.short}
                                        </div>
                                      )}
                                      <Select
                                        value={deco.primary}
                                        onValueChange={(v) => changeDecoration(order, it.item_index, v)}
                                      >
                                        <SelectTrigger
                                          style={{ height: 22, marginTop: 4, fontSize: 10, padding: '0 6px' }}
                                          data-testid={`deco-select-${order.id}-${it.item_index}`}
                                          onClick={(e) => e.stopPropagation()}>
                                          <SelectValue placeholder="Change method" />
                                        </SelectTrigger>
                                        <SelectContent>
                                          {DECO_METHODS.map((m) => (
                                            <SelectItem key={m.value} value={m.value}>
                                              {m.emoji} {m.label}
                                            </SelectItem>
                                          ))}
                                        </SelectContent>
                                      </Select>
                                      {deco.mixed && (
                                        <p style={{ fontSize: 8, color: '#92400e', margin: '3px 0 0', fontWeight: 600 }}>
                                          per-location set on Proposal
                                        </p>
                                      )}
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )}

                        {/* Per-item Purchasing strip — operator can mark shirts received while hats are still to be ordered. */}
                        {(orderPurchasing[order.id] || []).length > 0 && (
                          <div style={{ marginTop: 14 }} data-testid={`order-purchasing-${order.id}`}>
                            <p style={{ fontSize: '11px', color: '#9ca3af', margin: 0, textTransform: 'uppercase', marginBottom: 6 }}>
                              Purchasing ({(orderPurchasing[order.id] || []).length})
                            </p>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 8 }}>
                              {(orderPurchasing[order.id] || []).map((pi) => {
                                const meta = purchStatusMeta(pi.status);
                                return (
                                  <div
                                    key={pi.id}
                                    data-testid={`purchasing-row-${pi.id}`}
                                    style={{
                                      display: 'flex', flexDirection: 'column', gap: 6,
                                      background: meta.bg, border: `1px solid ${meta.border}`,
                                      borderRadius: 8, padding: 10,
                                    }}
                                  >
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                      <span style={{ fontSize: 16 }}>{meta.emoji}</span>
                                      <div style={{ flex: 1, minWidth: 0 }}>
                                        <p style={{ fontSize: 13, fontWeight: 700, color: '#0f172a', margin: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                          {pi.brand} {pi.style}
                                        </p>
                                        <p style={{ fontSize: 11, color: meta.fg, margin: 0, fontWeight: 600 }}>
                                          {pi.color} · ×{pi.total_qty}
                                        </p>
                                      </div>
                                    </div>
                                    <Select
                                      value={pi.status}
                                      onValueChange={(v) => changePurchasingStatus(order.id, pi.id, v)}
                                    >
                                      <SelectTrigger
                                        data-testid={`purchasing-status-${pi.id}`}
                                        style={{
                                          height: 30, fontSize: 11, fontWeight: 700,
                                          padding: '0 10px', background: '#fff', borderColor: meta.border, color: meta.fg,
                                        }}>
                                        <SelectValue />
                                      </SelectTrigger>
                                      <SelectContent>
                                        {PURCHASING_STATUSES.map((s) => (
                                          <SelectItem key={s.value} value={s.value}>
                                            {s.emoji} {s.label}
                                          </SelectItem>
                                        ))}
                                      </SelectContent>
                                    </Select>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )}

                        {/* Customer Provided Products — items the customer
                            dropped off (no purchasing needed). We still want
                            a completion checkmark per item so the production
                            team can confirm they've handled it. The check
                            mirrors the parent order's Completed status. */}
                        {(orderCppItems[order.id] || []).length > 0 && (
                          <div style={{ marginTop: 14 }} data-testid={`order-cpp-${order.id}`}>
                            <p style={{ fontSize: '11px', color: '#9ca3af', margin: 0, textTransform: 'uppercase', marginBottom: 6 }}>
                              Customer Provided ({(orderCppItems[order.id] || []).length})
                            </p>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 8 }}>
                              {(orderCppItems[order.id] || []).map((cpp) => {
                                const done = !!cpp.completed;
                                return (
                                  <div
                                    key={`cpp-${order.id}-${cpp.item_index}`}
                                    data-testid={`cpp-row-${order.id}-${cpp.item_index}`}
                                    style={{
                                      display: 'flex', alignItems: 'center', gap: 10,
                                      background: done ? '#ecfdf5' : '#fffbeb',
                                      border: `1px solid ${done ? '#86efac' : '#fde68a'}`,
                                      borderRadius: 8, padding: 10,
                                    }}
                                  >
                                    <span style={{ fontSize: 18 }}>{done ? '✅' : '📦'}</span>
                                    <div style={{ flex: 1, minWidth: 0 }}>
                                      <p style={{ fontSize: 13, fontWeight: 700, color: '#0f172a', margin: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                        {cpp.brand} {cpp.style}
                                      </p>
                                      <p style={{ fontSize: 11, color: done ? '#047857' : '#92400e', margin: 0, fontWeight: 600 }}>
                                        {cpp.color || 'Various'} · ×{cpp.total_qty}
                                        {done ? ' · Completed' : ' · Awaiting completion'}
                                      </p>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )}

                        {/* Production Notes — time-stamped, append-only log so
                            the shop floor and ops have full historical context
                            of every instruction added to the order. */}
                        <div style={{ marginTop: 14, padding: '12px 14px', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 10 }}
                          data-testid={`order-production-notes-${order.id}`}>
                          <TimestampedNotes
                            entries={order.production_notes_log || []}
                            addUrl={`/orders/${order.id}/production-note`}
                            deleteUrlPrefix={`/orders/${order.id}/production-note`}
                            placeholder="Add a production note (e.g. rush — needs to ship by Friday, use white thread, ink color match required)…"
                            title="Order Production Notes"
                            accentColor="#92400e"
                            testIdPrefix={`order-pn-${order.id}`}
                            onChange={({ entries, latestText }) => {
                              // optimistic update — refresh full list on next fetch
                              setOrders((prev) => prev.map((o) =>
                                o.id === order.id
                                  ? { ...o, production_notes_log: entries, notes: latestText }
                                  : o
                              ));
                            }}
                          />
                        </div>

                        {/* Completion section — shows the timestamp + signer when the order
                            has been closed, with a button to mark it complete or reopen. */}
                        <div style={{ marginTop: 14 }} data-testid={`order-completion-${order.id}`}>
                          {['Ready for Pickup', 'Shipped', 'Delivered'].includes(order.status) && (
                            <div data-testid={`notify-row-${order.id}`} style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 10, padding: '10px 14px', background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 10 }}>
                              <Button size="sm" data-testid={`notify-customer-${order.id}`} onClick={() => setNotifyOrder(order)} style={{ background: order.pickup_notification?.sent_at ? '#fff' : '#0f766e', color: order.pickup_notification?.sent_at ? '#0f766e' : '#fff', border: '1px solid #0f766e', fontSize: 12 }}>
                                <Mail size={14} style={{ marginRight: 6 }} />{order.pickup_notification?.sent_at ? 'Resend notification' : 'Notify Customer'}
                              </Button>
                              {order.pickup_notification?.sent_at ? (
                                <span data-testid={`notify-status-${order.id}`} style={{ fontSize: 12, color: '#065f46' }}>
                                  Customer notified {new Date(order.pickup_notification.sent_at).toLocaleString()} · {order.pickup_notification.to}
                                  {order.pickup_notification.mode === 'mock' && <span style={{ marginLeft: 6, background: '#fef3c7', color: '#92400e', padding: '2px 6px', borderRadius: 4, fontWeight: 700 }}>MOCK — no real email sent</span>}
                                </span>
                              ) : (
                                <span data-testid={`notify-status-${order.id}`} style={{ fontSize: 12, color: '#475569' }}>Customer has not been notified yet.</span>
                              )}
                            </div>
                          )}
                          {order.status === 'Completed' && order.completed_at ? (
                            <div style={{
                              background: 'linear-gradient(90deg, #ecfdf5, #f0fdf4)',
                              border: '1px solid #86efac', borderRadius: 10,
                              padding: '10px 14px',
                              display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap',
                            }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                <div style={{
                                  width: 32, height: 32, borderRadius: '50%', background: '#16a34a',
                                  color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, fontWeight: 700,
                                }}>✓</div>
                                <div>
                                  <p style={{ fontSize: 13, fontWeight: 700, color: '#166534', margin: 0 }}>
                                    Order Completed
                                  </p>
                                  <p style={{ fontSize: 11, color: '#15803d', margin: '2px 0 0' }}>
                                    {formatDate(order.completed_at)} · {new Date(order.completed_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                    {order.completed_by ? ` · ${order.completed_by}` : ''}
                                  </p>
                                </div>
                              </div>
                              <Button
                                size="sm" variant="ghost"
                                data-testid={`reopen-order-${order.id}`}
                                onClick={async () => {
                                  try {
                                    await axios.post(`/orders/${order.id}/reopen`);
                                    toast.message('Order reopened');
                                    await fetchOrders();
                                  } catch (err) {
                                    toast.error(err.response?.data?.detail || 'Failed to reopen');
                                  }
                                }}
                                style={{ color: '#475569', fontSize: 12 }}>
                                <RefreshCw size={12} style={{ marginRight: 4 }} />Reopen
                              </Button>
                            </div>
                          ) : (
                            <Button
                              size="sm"
                              data-testid={`mark-completed-${order.id}`}
                              onClick={async () => {
                                if (!window.confirm('Mark this order as Completed?\n\nThis records a timestamp and signs you off as the closer.')) return;
                                try {
                                  await axios.post(`/orders/${order.id}/complete`);
                                  toast.success('Order marked Completed');
                                  await fetchOrders();
                                } catch (err) {
                                  toast.error(err.response?.data?.detail || 'Failed to complete order');
                                }
                              }}
                              style={{ background: '#16a34a', color: '#fff', fontSize: 12 }}>
                              <Check size={14} style={{ marginRight: 6 }} />Mark Order Completed
                            </Button>
                          )}
                        </div>
                      </div>
                      
                      {/* Status Badge & Actions */}
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '10px' }}>
                        <span style={{
                          padding: '5px 14px',
                          borderRadius: '4px',
                          fontSize: '11px',
                          fontWeight: '700',
                          background: statusStyle.bg,
                          color: statusStyle.color,
                          letterSpacing: '0.5px'
                        }}>
                          {statusStyle.text}
                        </span>
                        
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          onClick={() => startEditing(order)}
                          style={{ color: '#6b7280', fontSize: '13px' }}
                        >
                          <Edit2 size={14} /> Edit
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={async () => {
                            const url = (await axios.get(`/orders/${order.id}/share-link`)).data.url;
                            try {
                              navigator.clipboard.writeText(url);
                              toast.success('Tracking link copied to clipboard');
                            } catch {
                              window.prompt('Copy this tracking link:', url);
                            }
                          }}
                          data-testid={`share-order-${order.id}`}
                          title="Copy public tracking link for the customer"
                          style={{ color: '#0891b2', fontSize: '13px' }}>
                          <Share2 size={14} /> Share Link
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          onClick={() => navigate(`/design-studio?orderId=${order.id}`)}
                          style={{ color: '#8b5cf6', fontSize: '13px' }}
                        >
                          <Palette size={14} /> Design
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={async () => {
                            try {
                              const r = await axios.post(`/purchasing/from-order/${order.id}`);
                              toast.success(r.data?.message || `Synced ${r.data?.created || 0} items`);
                            } catch (e) {
                              toast.error(e.response?.data?.detail || 'Sync failed');
                            }
                          }}
                          style={{ color: '#0ea5e9', fontSize: '13px' }}
                          data-testid={`sync-purchasing-${order.id}`}
                          title="Re-create any missing purchasing items for this order"
                        >
                          <RefreshCw size={14} /> Sync Purchasing
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={async () => {
                            if (!window.confirm(`Delete order ${order.po_number || order.id.slice(0,8)}?\n\nThis will ALSO delete the linked proposal and its purchasing items. This cannot be undone.`)) return;
                            try {
                              const r = await axios.delete(`/orders/${order.id}?cascade=true`);
                              toast.success(`Order deleted · purged ${r.data?.purchasing_items_deleted || 0} purchasing item(s)${r.data?.proposal_deleted ? ' + proposal' : ''}`);
                              await fetchOrders();
                            } catch (e) {
                              toast.error(e.response?.data?.detail || 'Delete failed');
                            }
                          }}
                          style={{ color: '#dc2626', fontSize: '13px' }}
                          data-testid={`delete-order-${order.id}`}
                          title="Cascade-delete this order, its proposal, and its purchasing items"
                        >
                          <Trash2 size={14} /> Delete
                        </Button>
                      </div>
                    </div>
                  )}
                  {editingOrder !== order.id && <OrderShippingCard order={order} isAdmin={user?.role === 'admin'} onChanged={(upd) => setOrders((prev) => prev.map((o) => (o.id === upd.id ? { ...o, status: upd.status, tracking_number: upd.tracking_number, fulfillment: upd.fulfillment } : o)))} />}
                </div>
              );
            })}
          </div>
        )}
      </main>
    </div>
  );
};

export default OrderTracking;
